import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MdChevronRight } from 'react-icons/md';
import deltaService from '../../services/deltaService';
import { GOVERNANCE } from '../../content/deck';
import { Chip, Section } from './DeltaUI';
import { Footnote, Note, SlideHead, StatementBar, StepRail, toneVars, TONES } from './DeckUI';

// The policy pack — the deck's governance slide, with live status from the register.
//
// The deck names twenty-three documents; the register seeded from Charter v1.0 holds
// fifty-four. That is not a bug in either — the deck is a deliberate consolidation, and
// says so on its own face ("Twenty-three policies, not fifty"). Both are shown: the pack
// as presented, and the full register underneath, with the difference stated rather than
// quietly resolved. Reconciling the two is the CIO's decision, not this screen's.
//
// STATUS IS JOINED BY EXACT REFERENCE, NEVER BY TITLE. Each entry in the deck content
// carries `ref` — a register reference, or null. A similarity match between "Core Book
// IPS" and "Book IPS — Core Investment" would work until the day it attached one policy's
// approval to another, and a governance screen that misreports what is in force is worse
// than one that reports nothing. Where there is no mapping, no status is shown.

const STATUS_CHIP = {
  in_force: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
  drafted: 'bg-blue-500/10 text-blue-700 dark:text-blue-400',
  drafting: 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
  not_started: 'bg-gray-500/10 text-gray-500 dark:text-gray-400',
  withdrawn: 'bg-rose-500/10 text-rose-700 dark:text-rose-400',
};

const TIER_CHIP = {
  T1: 'bg-rose-500/10 text-rose-700 dark:text-rose-400',
  T2: 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
  T3: 'bg-gray-500/10 text-gray-500 dark:text-gray-400',
};

export default function PolicyPackPage() {
  const [register, setRegister] = useState(null);

  useEffect(() => {
    deltaService.getPolicyRegister().then(setRegister).catch(() => setRegister(null));
  }, []);

  // reference → policy, for the exact-match join above.
  const byRef = useMemo(() => {
    const all = [...(register?.governing || []), ...(register?.baskets || []).flatMap((b) => b.policies)];
    return Object.fromEntries(all.map((p) => [p.reference, p]));
  }, [register]);

  const registerTotal = Object.keys(byRef).length;
  const deckTotal = GOVERNANCE.columns.reduce((n, c) => n + c.policies.length, 0) + 1; // + the Charter

  return (
    <div>
      <SlideHead headline={GOVERNANCE.headline} />

      {/* The Charter is displayed apart from the three groups because it sits above them —
          but it is still a DOCUMENT, and it was the one entry on this screen with no way
          to open it. It is the first thing anyone drafts, so it must be the easiest to
          reach, not the only thing unreachable. */}
      <CharterCard live={byRef[GOVERNANCE.charter.ref]} />

      {/* Three groups, stacked on a phone rather than selected one at a time. Unlike the
          three books, these are lists of seven — short enough to read straight through,
          and they have no flows between them that a selector would need to preserve. */}
      <div className="mt-4 grid gap-3 lg:grid-cols-3 lg:items-start">
        {GOVERNANCE.columns.map((c) => (
          <section key={c.key}
                   className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-800 dark:bg-[#0d1117]"
                   style={toneVars(c.tone)}>
            <header className="px-3 py-2.5 text-center sm:px-4" style={{ background: TONES[c.tone].bar }}>
              <h2 className="text-[12px] font-semibold uppercase tracking-[1.5px] text-white sm:text-[13px]">
                {c.title}
              </h2>
              <p className="mt-0.5 text-[10px] text-white/70">{c.tagline}</p>
            </header>
            <ul className="divide-y divide-gray-100 dark:divide-gray-800">
              {c.policies.map((p) => {
                const live = p.ref ? byRef[p.ref] : null;
                // Only a document that exists on the register can be opened. An entry with
                // no counterpart is a name in the plan, not something with a page.
                const body = (
                  <>
                    <div className="min-w-0 flex-1">
                      <div className={`text-[12.5px] leading-snug ${
                        p.sub
                          ? 'italic text-gray-500 dark:text-gray-400'
                          : 'text-gray-900 dark:text-gray-100'
                      }`}>
                        {p.sub && <span className="mr-1 text-gray-400">↳</span>}
                        {p.title}
                      </div>
                      {live && (
                        <Chip className={`mt-1 ${STATUS_CHIP[live.status]}`}>{live.status_display}</Chip>
                      )}
                    </div>
                    <Chip className={`shrink-0 ${TIER_CHIP[p.tier]}`}>{p.tier}</Chip>
                    {live
                      ? <MdChevronRight size={16} className="mt-0.5 shrink-0 text-gray-300 dark:text-gray-600" />
                      : <span className="w-4 shrink-0" aria-hidden="true" />}
                  </>
                );
                const cls = `flex items-start gap-2 px-3 py-2.5 ${p.sub ? 'pl-6' : ''}`;
                return (
                  <li key={p.title}>
                    {live ? (
                      <Link to={`/policies/${encodeURIComponent(live.reference)}`}
                            className={`${cls} transition-colors hover:bg-black/[0.03] dark:hover:bg-white/[0.04]`}>
                        {body}
                      </Link>
                    ) : (
                      <div className={cls}>{body}</div>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>

      <div className="mt-4">
        <Note>{GOVERNANCE.meantime}</Note>
      </div>

      <StepRail label={GOVERNANCE.implementation.label} steps={GOVERNANCE.implementation.steps} />

      <p className="mt-3 text-[11.5px] leading-relaxed text-gray-500 dark:text-gray-400">
        {GOVERNANCE.totalNote}
      </p>

      {/* The discrepancy, stated. Not a warning banner — neither number is wrong, and the
          reader is being told which document they are looking at. */}
      {registerTotal > 0 && registerTotal !== deckTotal && (
        <p className="mt-2 text-[11.5px] leading-relaxed text-gray-500 dark:text-gray-400">
          The pack above is the {deckTotal}-document set as presented. The register below still
          holds the {registerTotal} documents enumerated in Charter v1.0, which this pack
          consolidates. Statuses are shown against the {Object.values(byRef).length ? 'documents that map across' : 'register'}; the
          rest are not yet on the register.
        </p>
      )}

      <div className="mt-4">
        <Section title="Full register — Charter v1.0"
                 subtitle={`${registerTotal || '—'} documents, before consolidation`}
                 defaultOpen={false}>
          <FullRegister register={register} />
        </Section>
      </div>

      <Footnote>{GOVERNANCE.footnote}</Footnote>
    </div>
  );
}

// The Charter, as a way in. Rendered as a link whenever it is on the register, with a
// visible affordance — the previous version looked identical to a static banner, so nobody
// would have guessed it opened anything.
function CharterCard({ live }) {
  const inner = (
    <StatementBar title={GOVERNANCE.charter.title} tone="navy">
      {/* Lower case, as in the deck. Set in tracked capitals it ran to four lines on a
          phone and read as a heading rather than the Charter's contents list. */}
      <p className="text-[11.5px] text-gray-500 dark:text-gray-400">{GOVERNANCE.charter.tagline}</p>
      <p className="italic">{GOVERNANCE.charter.derivation}</p>
      <div className="flex flex-wrap items-center gap-2">
        {live && <Chip className={STATUS_CHIP[live.status]}>{live.status_display}</Chip>}
        {live && (
          <span className="flex items-center gap-0.5 text-[11px] font-medium text-brand-gold">
            Open the document <MdChevronRight size={14} />
          </span>
        )}
      </div>
    </StatementBar>
  );
  if (!live) return inner;
  return (
    <Link to={`/policies/${encodeURIComponent(live.reference)}`}
          className="block transition-opacity hover:opacity-90">
      {inner}
    </Link>
  );
}

// The unconsolidated register, read-only. Kept because a policy not on the register is not
// in force (Charter Schedule 2) — so the register stays visible even once the pack above
// supersedes its grouping.
function FullRegister({ register }) {
  if (!register) return <div className="py-6 text-center text-sm text-gray-400">Loading…</div>;
  const groups = [
    { name: 'Governing documents', policies: register.governing || [] },
    ...(register.baskets || []).map((b) => ({ name: b.basket, policies: b.policies })),
  ];
  return (
    <div className="space-y-4">
      {groups.map((g) => (
        <div key={g.name}>
          <div className="mb-1.5 text-[9px] uppercase tracking-[2px] text-gray-400 dark:text-gray-500">
            {g.name} · {g.policies.length}
          </div>
          <ul className="divide-y divide-gray-100 dark:divide-gray-800">
            {g.policies.map((p) => (
              <li key={p.id} className="flex items-center gap-2 py-1.5">
                <span className={`min-w-0 flex-1 text-[12px] ${
                  p.is_heavy ? 'font-semibold text-gray-900 dark:text-gray-100' : 'text-gray-700 dark:text-gray-300'
                }`}>
                  {p.title}
                </span>
                <Chip className={STATUS_CHIP[p.status]}>{p.status_display}</Chip>
                <span className="w-7 shrink-0 text-right text-[10px] text-gray-400">T{p.tier ?? '—'}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
